import React, { useMemo } from 'react';
import loadable from '@loadable/component';
import styled from 'libs/styled';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import type { ReferencesMap } from '@fogia/core-web/utils/internalRefToSlug';
import { mapPageReferenceToProductCard } from 'components/sliders/ProductSlider/mapPageReferenceProductToProductCard';
import type { ProductCardProduct } from 'components/product/ProductCard';
import type {
    ProductSliderContentGroup,
    ProductSliderModule as ProductSliderModuleData,
} from 'libs/sanity/types/generated';

const ModuleWrapper = styled('section')`
    position: relative;
    margin: var(--module-margin-y) 0;
`;

const withDefault = <T,>(mod: { default?: T } | null | undefined): { default: T } => ({
    default: (mod?.default ?? (() => null)) as T,
});

const ProductSliderSection = loadable(() => import('content/ProductSlider').then((mod) => withDefault(mod)));

const EMPTY_PAGE_REFERENCES: ReferencesMap = {};

const mapItemsToProducts = (
    items: ProductSliderContentGroup['items'] | undefined,
    pageReferences: ReferencesMap,
): ProductCardProduct[] => {
    const ordered: ProductCardProduct[] = [];
    if (!items?.length) {
        return ordered;
    }
    items.forEach((item) => {
        const rawId = item?.productId;
        if (rawId == null || rawId === '') {
            return;
        }
        const key = String(rawId).trim();
        if (!key) {
            return;
        }
        const refEntry = pageReferences[key];
        const card = mapPageReferenceToProductCard(refEntry, key);
        if (card) {
            ordered.push(card);
        }
    });
    return ordered;
};

interface ProductSliderModuleProps {
    data?: ProductSliderModuleData;
}

const ProductSliderModule: React.FC<ProductSliderModuleProps> = (props) => {
    const { data: moduleData } = props;

    const rawPageReferences = useAppSelector((state) => state.page?.data?.references) as ReferencesMap | undefined;
    const pageReferences: ReferencesMap = rawPageReferences ?? EMPTY_PAGE_REFERENCES;

    const groups = useMemo(
        () =>
            (moduleData?.groups ?? []).map((group) => ({
                key: group.key,
                groupTitle: group.groupTitle,
                products: mapItemsToProducts(group.items, pageReferences),
            })),
        [moduleData?.groups, pageReferences],
    );

    return (
        <ModuleWrapper>
            <ProductSliderSection groups={groups} link={moduleData?.link} />
        </ModuleWrapper>
    );
};

export default ProductSliderModule;
